import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import StreamDestinationSettings from './templates/stream-destination-settings';
import EventSettings from './templates/event-settings';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import { GreenroomState as GS } from '../../stores/types/types';
import AvatarPhoto from './templates/avatar-photo';
import { setModalOpen } from '../../stores/actions/modal';
import ShotBuilder from './templates/shot-builder/shot-builder';
import DeleteShotConfirmation from './templates/delete-shot-confirmation';
import CopyEvent from './templates/copy-event';
import DeleteEventConfirmation from './templates/delete-event-confirmation';
import ProgramFeed from './templates/program-feed';
import VideoEditor from './templates/video-editor/video-editor';
import OnsiteFeed from './templates/onsite-feed';
import DeleteAccount from './templates/delete-account';
import MultiAccountLoginConfirmation from './templates/multi-account-login-confirmation';
import LiveStreamConfirmation from './templates/live-stream-confirmation';
import Socket from '../../connection/socket';
import DeleteChatMessageConfirmation from './templates/delete-chat-message-confirmation';
import NoRoomAccess from './templates/no-room-access';
import useCloseEditModal from '../../hooks/use-close-edit-modal';
import MediaLibraryContainer from './templates/media-library/container';
import DevicePermission from './templates/device-permission';
import BroadcastFailed from './templates/broadcast-failed';
import RecordingFailed from './templates/recording-failed';
import DestinationFailed from './templates/destination-failed';
import MultiOnsiteLocation from './templates/multi-onsite-location';
import DeleteMask from './templates/delete-mask';
import ProductTour from './templates/product-tour';
import OnsiteLocation from './templates/onsite-location';
import DeleteThemeModal from './templates/themes/delete-theme';
import DeleteChatRoomConfirmation from './templates/delete-chat-room-confirmation ';
import ImageCropper from './templates/image-cropper';
import GeneratedContentUpdating from './templates/generated-content-updating';

interface ModalProps {
	socket?: Socket | null | undefined
}

export default function Modal(props: ModalProps) {
	const { socket } = props;

	const dispatch = useDispatch();
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
	const modalTemplate = useSelector((s: GS) => s.ModalReducer.modalTemplate);
	const modalData = useSelector((s: GS) => s.ModalReducer.modalData);
	const upsertingContent = useSelector((s: GS) => s.GeneratedContentReducer.upsertingContent);

	const modalBackground = useRef<HTMLDivElement>(null);

	const closeEditModal = useCloseEditModal();

	const MODALS_REQUIRING_CLICK_TO_CLOSE = [
		MODAL_TEMPLATES.PROGRAM_FEED,
		MODAL_TEMPLATES.MULTI_ACCOUNT_LOGGED_IN,
		MODAL_TEMPLATES.AVATAR_PHOTO,
		MODAL_TEMPLATES.SAFETY_PHOTO,
		MODAL_TEMPLATES.BROADCAST_FAILED,
		MODAL_TEMPLATES.DESTINATION_FAILED,
		MODAL_TEMPLATES.MULTI_ONSITE_LOCATION,
		MODAL_TEMPLATES.GR_PICTURE
	];

	const closeModal = (event: any) => {
		if (
			event.target === modalBackground.current
			&& !MODALS_REQUIRING_CLICK_TO_CLOSE?.includes(modalTemplate)
			&& modalTemplate !== MODAL_TEMPLATES.ONSITE_LOCATION // this modal has its own close logic
			&& !upsertingContent
		) {
			if (
				modalTemplate === MODAL_TEMPLATES.SHOT_BUILDER
				|| modalTemplate === MODAL_TEMPLATES.DELETE_SHOT_CONFIRMATION
			) {
				closeEditModal(socket, false);
			} else {
				dispatch(setModalOpen(false));
			}
		}
	};

	return (
		modalOpen ? (
			<div
				id='modal'
				className={`info-modal-background ${modalTemplate}`}
				ref={modalBackground}
				onClick={closeModal}
			>
				{modalTemplate === MODAL_TEMPLATES.DESTINATION_FAILED && <DestinationFailed />}
				{modalTemplate === MODAL_TEMPLATES.BROADCAST_FAILED && <BroadcastFailed />}
				{modalTemplate === MODAL_TEMPLATES.RECORDING_FAILED && <RecordingFailed />}
				{modalTemplate === MODAL_TEMPLATES.DEVICE_PERMISSION && <DevicePermission />}
				{modalTemplate === MODAL_TEMPLATES.MEDIA_LIBRARY && <MediaLibraryContainer />}
				{modalTemplate === MODAL_TEMPLATES.EVENT_SETTINGS
					&& (
					<EventSettings
						showSession={modalData.showSession}
						showSessionTypes={modalData.showSessionTypes}
						socket={modalData.socket}
						modalInfoAction={modalData.modalInfoAction}
						showInvitations={modalData.showInvitations}
					></EventSettings>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.STREAM_DESTINATION_SETTINGS
					&& <StreamDestinationSettings
					destinationType={modalData.destinationType}
					></StreamDestinationSettings>}
				{modalTemplate === MODAL_TEMPLATES.AVATAR_PHOTO
					&& (
						<AvatarPhoto
							isOnboarding={modalData.isOnboarding}
							modalAvatarAction={modalData.modalAvatarAction}
						></AvatarPhoto>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.GR_PICTURE
					&& (
						<ImageCropper
							mode={modalData.mode}
							onConfirm={modalData.onConfirm}
							onClose={modalData.onClose}
							allowCamera={modalData.allowCamera}
							cameraStream={modalData.cameraStream}
							mirrorCamera={modalData.mirrorCamera}
							accept={['png', 'jpg', 'jpeg']}
							labels={modalData.labels}
							initialImage={modalData.initialImage}
							mediaItem={modalData.mediaItem}
							onInvalidFileType={modalData.onInvalidFileType}
						></ImageCropper>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.DELETE_THEME
					&& <DeleteThemeModal/>
				}
				{modalTemplate === MODAL_TEMPLATES.SAFETY_PHOTO
					&& (
						<AvatarPhoto
							isOnboarding={modalData.isOnboarding}
							modalAvatarAction={modalData.modalAvatarAction}
						></AvatarPhoto>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.SHOT_BUILDER
					&& (
						<ShotBuilder
							socket={modalData.socket}
							modalPage={modalData.modalPage}
							modalTab={modalData.modalTab}
							modalGraphicsLoading={modalData.modalGraphicsLoading}
						/>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.DELETE_SHOT_CONFIRMATION
					&& <DeleteShotConfirmation socket={modalData.socket} />
				}
				{modalTemplate === MODAL_TEMPLATES.COPY_EVENT
					&& <CopyEvent sourceMeeting={modalData.sourceMeeting} />
				}
				{modalTemplate === MODAL_TEMPLATES.DELETE_EVENT_CONFIRMATION
					&& <DeleteEventConfirmation meeting={modalData.meeting} />
				}
				{modalTemplate === MODAL_TEMPLATES.PROGRAM_FEED
					&& <ProgramFeed />
				}
				{modalTemplate === MODAL_TEMPLATES.ONSITE_FEED
					&& <OnsiteFeed />
				}
				{modalTemplate === MODAL_TEMPLATES.VIDEO_EDITOR
					&& (
						<VideoEditor
							mediaId={modalData.mediaId}
							editorPage={modalData.editorPage}
						/>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.DELETE_ACCOUNT
					&& (
						<DeleteAccount
						/>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.DELETE_MASK_CONFIRMATION
					&& (<DeleteMask email={modalData.email}/>)
				}
				{modalTemplate === MODAL_TEMPLATES.MULTI_ACCOUNT_LOGGED_IN && (
					<MultiAccountLoginConfirmation socket={socket} />
				)}
				{modalTemplate === MODAL_TEMPLATES.LIVE_STREAM_CONFIRMATION && (
					<LiveStreamConfirmation
						socket={modalData.socket}
						initialMode={modalData.initialMode}
					/>
				)}
				{modalTemplate === MODAL_TEMPLATES.DELETE_CHAT_MESSAGE_CONFIRMATION && (
					<DeleteChatMessageConfirmation
						chatPost={modalData.chatPost}
						socket={modalData.socket}
					/>
				)}
				{modalTemplate === MODAL_TEMPLATES.DELETE_CHAT_ROOM_CONFIRMATION && (
					<DeleteChatRoomConfirmation
						room={modalData.room}
						socket={modalData.socket}
					/>
				)}
				{modalTemplate === MODAL_TEMPLATES.NO_ROOM_ACCESS && (
					<NoRoomAccess isProducer={modalData?.host} forReason={modalData?.reason}/>
				)}
				{modalTemplate === MODAL_TEMPLATES.PRODUCT_TOUR && (
					<ProductTour />
				)}
				{modalTemplate === MODAL_TEMPLATES.MULTI_ONSITE_LOCATION && (
					<MultiOnsiteLocation
						socket={modalData.socket}
						onsiteLocationId={modalData.onsiteLocationId}
						session={modalData.session}
						handlePublish={modalData.handlePublish}
					/>
				)}
				{modalTemplate === MODAL_TEMPLATES.ONSITE_LOCATION
					&& <OnsiteLocation />
				}
				{modalTemplate === MODAL_TEMPLATES.GENERATED_CONTENT_UPDATING && (
					<GeneratedContentUpdating 
						staleContent={modalData.staleContent ?? []}
					/>
				)}
			</div>
		) : <></>
	);
}
